import { useCallback, useState } from 'react';
import { AgenciesService } from '@services/requests/housien-services/AgenciesService';

export interface UseCancelSubscriptionResult {
  cancel: () => Promise<void>;
  isCancelling: boolean;
  isCancelled: boolean;
  cancelledAt: string | null;
  isError: boolean;
}

/**
 * Provides a cancel() action for an agency subscription.
 *
 * Calls DELETE /agencies/{agencyId}/subscription. isCancelled/cancelledAt here
 * are an optimistic overlay for the moment right after the call succeeds —
 * the source of truth on page load is the subscription GET query, since this
 * state resets on every remount (e.g. a page reload). Callers should pass
 * onCancelled to refetch that query so the server becomes authoritative again.
 */
export function useCancelSubscription(
  agencyId: string,
  onCancelled?: () => void,
): UseCancelSubscriptionResult {
  const [isCancelling, setIsCancelling] = useState(false);
  const [isCancelled, setIsCancelled] = useState(false);
  const [cancelledAt, setCancelledAt] = useState<string | null>(null);
  const [isError, setIsError] = useState(false);

  const cancel = useCallback(async () => {
    setIsCancelling(true);
    setIsError(false);
    try {
      const result = await AgenciesService.cancelSubscription(agencyId);
      setIsCancelled(true);
      setCancelledAt(result.cancelled_at);
      onCancelled?.();
    } catch {
      setIsError(true);
    } finally {
      setIsCancelling(false);
    }
  }, [agencyId, onCancelled]);

  return { cancel, isCancelling, isCancelled, cancelledAt, isError };
}
